import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DogEarCard } from '@audiq/ui';
import type { Conditions, FlowOutcome, FlowOutcomeUpdate, FlowWithRelations } from '@audiq/shared';
import { api } from '../../lib/api';
import { ConditionBuilder, extractAvailableFlags } from './ConditionBuilder';

interface Props {
  flow: FlowWithRelations;
}

export function OutcomesTab({ flow }: Props) {
  const qc = useQueryClient();
  const outcomes = flow.outcomes;

  const createMut = useMutation({
    mutationFn: () =>
      api.createOutcome(flow.id, {
        sort_order: outcomes.length,
        name: 'Nuovo esito',
        description: '',
        conditions: { op: 'AND', rules: [] },
        action: { type: 'info', content: '', meta: null },
        priority: 100,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow', flow.id] }),
  });

  const updateMut = useMutation({
    mutationFn: ({ oid, data }: { oid: string; data: FlowOutcomeUpdate }) =>
      api.updateOutcome(flow.id, oid, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow', flow.id] }),
  });

  const deleteMut = useMutation({
    mutationFn: (oid: string) => api.deleteOutcome(flow.id, oid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow', flow.id] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
            Esiti possibili{' '}
            <span className="mono ms-2" style={{ color: 'var(--mute)' }}>
              {outcomes.length}
            </span>
          </h3>
          <p className="mt-1 text-sm" style={{ color: 'var(--mute)' }}>
            Le condizioni si compongono con il <strong>Condition Builder</strong> sotto ogni esito:
            AND/OR annidati su risposte raccolte, flag, lingua, emozione.
          </p>
        </div>
        <button
          type="button"
          onClick={() => createMut.mutate()}
          disabled={createMut.isPending}
          className="rounded-md px-3 py-1.5 text-sm"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          + Aggiungi esito
        </button>
      </div>

      {outcomes.length === 0 && (
        <DogEarCard className="rounded-md p-6 text-center">
          <p className="text-sm" style={{ color: 'var(--mute)' }}>
            Nessun esito configurato. Gli esiti vengono valutati a fine encounter sui dati raccolti
            e sui flag scattati.
          </p>
        </DogEarCard>
      )}

      <ol className="space-y-3">
        {outcomes.map((o) => (
          <li key={o.id}>
            <OutcomeCard
              outcome={o}
              flow={flow}
              onSave={(data) => updateMut.mutate({ oid: o.id, data })}
              onDelete={() => {
                if (confirm(`Eliminare l'esito "${o.name}"?`)) deleteMut.mutate(o.id);
              }}
              saving={updateMut.isPending}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

interface OutcomeCardProps {
  outcome: FlowOutcome;
  flow: FlowWithRelations;
  onSave: (data: FlowOutcomeUpdate) => void;
  onDelete: () => void;
  saving: boolean;
}

function OutcomeCard({ outcome, flow, onSave, onDelete, saving }: OutcomeCardProps) {
  const [draft, setDraft] = useState<FlowOutcomeUpdate>({
    name: outcome.name,
    description: outcome.description,
    priority: outcome.priority,
    action: outcome.action,
    conditions: outcome.conditions as Conditions,
  });
  const availableFlags = extractAvailableFlags(flow.questions);

  return (
    <DogEarCard className="rounded-md p-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium">Nome</label>
          <input
            type="text"
            value={draft.name ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">
            Priorità{' '}
            <span className="mono" style={{ color: 'var(--mute)' }}>
              (più basso = più importante)
            </span>
          </label>
          <input
            type="number"
            value={draft.priority ?? 100}
            onChange={(e) => setDraft((d) => ({ ...d, priority: Number(e.target.value) }))}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium">Descrizione</label>
          <textarea
            rows={2}
            value={draft.description ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium">
            Contenuto azione{' '}
            <span className="mono" style={{ color: 'var(--mute)' }}>
              (testo presentato all'utente)
            </span>
          </label>
          <textarea
            rows={3}
            value={draft.action?.content ?? ''}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                action: {
                  type: d.action?.type ?? 'info',
                  content: e.target.value,
                  meta: d.action?.meta ?? null,
                },
              }))
            }
            className="w-full rounded-md border px-3 py-2 text-sm"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium">Condizioni di attivazione</label>
          <ConditionBuilder
            value={(draft.conditions ?? { op: 'AND', rules: [] }) as Conditions}
            onChange={(next) => setDraft((d) => ({ ...d, conditions: next }))}
            questions={flow.questions}
            availableFlags={availableFlags}
          />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          onClick={onDelete}
          className="text-sm hover:underline"
          style={{ color: 'var(--danger)' }}
        >
          Elimina
        </button>
        <button
          type="button"
          onClick={() => onSave(draft)}
          disabled={saving}
          className="rounded-md px-3 py-1.5 text-sm disabled:opacity-50"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          {saving ? 'Salvo…' : 'Salva esito'}
        </button>
      </div>
    </DogEarCard>
  );
}
